import {observer} from 'mobx-react-lite';
import {Table} from '@/components/common-components/components';
import {ColumnsType} from 'antd/lib/table';
import {isNil, isEmpty} from 'lodash';
import {formatNumber} from '@/utils/number';
import {Tooltip} from 'antd';
import {VerticalBar} from '@/components/common-components/components/vertical-bar/index';
import styled from 'styled-components';
import {Button} from '@/components/common-components/v2/Button';
import React from 'react';
import {EmptyStateKeDetail} from '../../emptyStateKe';
import {getDomain, getSingleUrlParam, isPublicKeywordResearcherV2} from '@/utils/url';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {linkgraphDomains} from '@/utils/router';
import {useStore} from '@/store/root-store';


interface TableProps {
  tableData?: any[];
  loadingKeywordDetails?: boolean;
  totalValue?: any;
  redirectLink?: any;
  fromDrawer?: boolean;
}

export const KeywordsMiniTable:React.FC<TableProps> = observer(({tableData, loadingKeywordDetails, totalValue, redirectLink, fromDrawer}) => {
  const countryCode = getSingleUrlParam('countryCode');
  const locationId = getSingleUrlParam('locationId');
  const {
    countries: {getSpecificCountryByCode}} = useStore('');
  const router = useRouter();
  const publicHash = router?.query?.public_hash;
  const currentDomain = getDomain(window.location.hostname);
  const isWhiteLabelDomain = !linkgraphDomains.includes(currentDomain);
  const getLocationParms = () => {
    return `locationId=${locationId || ''}&countryCode=${countryCode || ''}&countryName=${countryCode ? getSpecificCountryByCode(countryCode)?.name : ''}`;
  };
  const columns: ColumnsType = [{
    title: () => <span>QUERY</span>,
    key: 'keyword',
    dataIndex: 'keyword',
    width: 250,
    align: 'left' as 'left',
    render: keyword => <div>
      <Tooltip title={keyword}>
        <Link legacyBehavior href={`/research/keyword-researcher${isPublicKeywordResearcherV2() ? '-public' : ''}/${'new'}/${keyword?.replaceAll(' ', '+')}?${getLocationParms()}${publicHash && isPublicKeywordResearcherV2() ? `&public_hash=${publicHash}` : ''}`} passHref>
          <a target={fromDrawer ? '_blank' : ''}>
            <Keyword>
              {!isEmpty(keyword) ? keyword : '-'}</Keyword>
          </a>
        </Link>
      </Tooltip>
    </div>,
  },
  {
    title: () => <Tooltip title={<TooltipDescriptionWrapper>
      {`The average number of monthly searches for the analyzed keyword over the last 12 months. This is useful in determining whether or not a keyword is worth targeting from an SEO perspective.`}
    </TooltipDescriptionWrapper>} placement={'bottomLeft'} color='#fff' overlayInnerStyle={{borderRadius: '6px', maxHeight: '200px', width: '300px'}}> <span>SV</span></Tooltip>,
    key: 'sv',
    dataIndex: 'sv',
    align: 'right',
    width: 70,
    render: sv => <div style={{display: 'flex', alignItems: 'center'}}> {!isNil(sv) ? (sv < 1000 ? sv?.toFixed(0) : String(formatNumber(sv, 1))?.replace('.0', '')) : '-'}</div>,
  },
  {
    title: () => <Tooltip title={<TooltipDescriptionWrapper>
      {`The price advertisers pay per click to target this keyword in Google Ads campaigns.`}
    </TooltipDescriptionWrapper>} placement={'bottomLeft'} color='#fff' overlayInnerStyle={{borderRadius: '6px', maxHeight: '200px', width: '300px'}}>  <span>CPC</span></Tooltip>,
    key: 'cpc',
    dataIndex: 'cpc',
    align: 'right',
    width: 70,
    render: cpc => <div style={{display: 'flex', alignItems: 'center', justifyContent: 'end'}}>
      {!isNil(cpc) ? `$${cpc?.toFixed(2)}` : '-'}
      <VerticalBar
        cpc={cpc}
        maxCpc={Math.max(...tableData.map(item => item.cpc))}
      />
    </div>,
  },
  ];
  return (
    <>
      {loadingKeywordDetails || tableData?.length ?<>
        <div style={{height: '270px'}}>
          <TableContainer>
            <Table
              dataSource={tableData}
              columns={columns}
              pagination={false}
              loading={loadingKeywordDetails}
              loadingRows={5}
            />
          </TableContainer>
        </div>
        {!loadingKeywordDetails && isWhiteLabelDomain && isPublicKeywordResearcherV2() ? '' :
          <Link legacyBehavior href={redirectLink != undefined ? redirectLink : ''} passHref>
            <a target={fromDrawer ? '_blank' : ''}>
              {!loadingKeywordDetails ? <ButtonStyled color='textGrayLight'>
              View all  {totalValue} keywords
              </ButtonStyled> : <></>}
            </a>
          </Link>}
      </> :
        <EmptyStateKeDetail isMiniTable={true} search={true} description={'Data Not found'}/>}
      {/* <SkeletonHorizontalLoaderGray marginTop='20px' skeletonMarginLeft='25px' width='100px' height='10px' skeletonHeight='10px' align='flex-start' lightLoader /> */}
    </>
  );
});

const ButtonStyled = styled(Button)`
  background: #F7F7F8;
  border: 1px solid #E8E8E8;
  border-radius: 5px;
  color: #121212 !important;
  margin-top: 6px;
`;

const Keyword = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2D6CCA;
  cursor: pointer;
  &:hover{
    text-decoration: underline;
  }
`;

const TableContainer = styled.div`
    /* display: flex;
    flex-direction: column;
    align-items: baseline;
    justify-content: space-between; */
    /* height: calc(100% - 47px); */

table tr th {
  background-color: #fff;
}

.ant-table-content {
  border-radius: 0 !important;
}

.ant-table-column-sorters-with-tooltip:not(:first-child) {
  text-align: center;
}

table {
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 10px;

  th::before {
    width: 0 !important;
  }

  .ant-table-column-sorter {
    display: flex;
    padding-left: 3px !important;
  }

  .ant-table-column-sorters {
    display: inline-flex !important;
    padding: 5px !important;
  }

  tr td {
    &:first-child {
      padding-bottom: 0 !important;
      padding-top: 0 !important;
    }
  }

  .ant-table-tbody {
    tr:nth-child(even) {
      td {
        background: #fff !important;
        border-bottom: 1px solid #f4f4f4 !important;
        box-shadow: none;
        font-weight: 400;
        white-space: nowrap;
      }

      &:hover {
        td {
          background: #eef4fe !important;
        }

        button {
          background: #eef4fe !important;
          border: 0 !important;
          color: #2d6cca;
          font-weight: 500;
        }
      }
    }

    tr:nth-child(odd) {
      td {
        background: #fff;
        border-bottom: 1px solid #f4f4f4 !important;
        box-shadow: none;
        font-weight: 400;
        white-space: nowrap;
      }

      &:hover {
        td {
          background: #eef4fe !important;
        }

        button {
          background: #eef4fe !important;
          border: 0 !important;
          color: #2d6cca;
          font-weight: 500;
        }
      }
    }
  }

  .ant-table-thead {
    background: #f7f7f7 !important;

    th {
      background: #f7f7f7 !important;
      color: #121212 !important;
      font-size: 12px;
      font-weight: 600;
      line-height: 14px;
      padding: 0 !important;
    }

    .ant-table-cell-fix-left {
      text-align: left !important;

      &:first-child {
        border-bottom-left-radius: 8px !important;
        border-top-left-radius: 8px !important;
      }
    }

    th:first-child {
      background: #f7f7f7 !important;
      border-bottom-left-radius: 8px !important;
      border-top-left-radius: 8px !important;
      color: #121212;
      font-size: 12px;
      font-weight: 600;
    }

    th:nth-child(2) {
      background: #f7f7f7 !important;
      color: #121212;
      font-size: 12px;
      font-weight: 600;
    }

    th:last-child {
      background-color: #f7f7f7 !important;
      border-bottom-right-radius: 8px !important;
      border-top-right-radius: 8px !important;
      padding-left: 25px !important;
    }
  }

  .ant-table-cell-fix-left-last {
    text-align: left !important;
  }

  tr:first-child td {
    background: #fff !important;
  }

  tr:nth-child(even) td {
    background: #f9f9fb !important;
  }

  tr:nth-child(odd) td {
    background-color: #fff !important;
  }

  tr th {
    height: 40px !important;
    min-height: 150px !important;
    line-height: 22px;
    white-space: nowrap;
  }

  .ant-column-sorters {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  tr td {
    padding-bottom: 15px;
    padding-top: 15px;
    text-align: center;
  }

  tr th:first-child,
  tr td:first-child {
    padding-left: 25px !important;
    padding-right: 25px !important;
  }

  tr th:last-child,
  tr td:last-child {
    padding-left: 25px !important;
    padding-right: 25px !important;
    text-align: center;
  }
}
`;
const TooltipDescriptionWrapper = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #121212;
  padding: 5px;
`;
